import Layout from "antd/lib/layout/layout";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { TableColumnsType, Typography } from "antd";
import color from "../../theme/color";
import Button from "../../components/atoms/Button";
import { theme } from "../../theme";
import SettlementFilterForm from "./components/SettlementFilterForm";
import { TableForm } from "../../components/organisms/Table";
import { TableFooterCount } from "../../components/organisms/Table/FooterCount";
import {
  SettlementListSearchQuery,
  SettlementStatusTypes,
} from "../../apis/Settlement/types";
import {
  fetchSettlements,
  fetchSettlementSummary,
  updateSettlementConfirm,
} from "../../apis/Settlement";
import RepairShopSettlementStatus from "../../components/organisms/Settlement/Tags/RepairShopSettlementStatus";
import { SettlementDto } from "../../model/Settlement";
import useModals from "../../hooks/useModals";
import { HttpError } from "../../network/types";
import { ReservationDetailDrawer } from "../../components/organisms/Reservation/RepairDetail";
import useReservation from "../../hooks/useReservation";
import useShop from "../../hooks/useShop";

export default function AdjustmentManager() {
  const { shop } = useShop();
  const queryClient = useQueryClient();
  const { openAlertModal } = useModals();
  const { reservation, setReservation } = useReservation();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState<SettlementListSearchQuery>({
    startedAt: moment().startOf("month").format("YYYY-MM-DD"),
    endedAt: moment().endOf("month").format("YYYY-MM-DD"),
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setReservation({});
  }, [shop]);

  const scrollY =
    (containerRef.current?.clientHeight || 0) -
    (filterRef.current?.clientHeight || 0) -
    24 * 2 -
    65 -
    24 * 2 -
    60 -
    24;

  const { mutate } = useMutation(updateSettlementConfirm, {
    onSuccess: () => {
      setSelectedRowKeys([]);
      queryClient.invalidateQueries(["Settlement", filters, shop]);
      queryClient.invalidateQueries(["SettlementSummary", filters, shop]);
      openAlertModal({
        title: "정산 확인 완료",
        contents: (
          <div>
            정산 확인되었습니다.
            <br />
            금액 확인 후 담당자가 세금계산서 발행을 요청드릴 예정입니다.
            <br />
            발행 요청 시 꼭 세금계산서를 해당 월 말까지 발행해주세요.
          </div>
        ),
      });
    },
    onError: (err: HttpError) => {
      openAlertModal({
        title: "정산 요청 실패",
        contents: err.message,
      });
    },
  });

  const { data } = useQuery(
    ["Settlement", filters, shop],
    () => fetchSettlements(filters),
    {
      enabled: !!filters.startedAt && !!filters.endedAt,
      staleTime: 1000 * 60 * 5,
      keepPreviousData: true,
    }
  );

  const { data: summary } = useQuery(
    ["SettlementSummary", filters, shop],
    () => fetchSettlementSummary(filters),
    {
      enabled: !!filters.startedAt && !!filters.endedAt,
      staleTime: 1000 * 60 * 5,
      keepPreviousData: true,
    }
  );

  const selectedList = data?.rows.filter(({ orderNo }) =>
    selectedRowKeys.includes(orderNo)
  );

  const haslSettlementRequested =
    selectedRowKeys.length > 0 &&
    selectedList?.some(
      ({ settlementStatus }) => settlementStatus === "SETTLEMENT_REQUESTED"
    );

  const payCompletedPrice =
    data?.rows
      .filter(({ settlementStatus }) => settlementStatus === "PAY_COMPLETED")
      .map(({ settlementAmount }) => settlementAmount)
      .reduce((acc, cur) => acc + cur, 0) ?? 0;

  const selectedPrice =
    selectedList
      ?.map(({ settlementAmount }) => settlementAmount)
      .reduce((acc, cur) => acc + cur, 0) ?? 0;

  const columns: TableColumnsType<SettlementDto> = [
    {
      title: "주문번호",
      dataIndex: "orderNo",
      key: "orderNo",
      width: 180,
      fixed: true,
    },
    {
      title: "예약일자",
      dataIndex: "reservedAt",
      key: "reservedAt",
      width: 120,
      render: (value) => moment(value).format("YYYY-MM-DD"),
    },
    {
      title: "차량번호",
      dataIndex: "customerVehicleNo",
      key: "customerVehicleNo",
      width: 100,
    },
    {
      title: "차종",
      dataIndex: "vehicleModel",
      key: "vehicleModel",
      width: 240,
      ellipsis: true,
      render: (value, recode) => `${recode.vehicleBrand} ${value}`,
    },
    {
      title: "상품카테고리",
      dataIndex: "productCategoryName",
      key: "productCategoryName",
      width: 120,
    },
    {
      title: "선택옵션",
      dataIndex: "selectedOptions",
      key: "selectedOptions",
      width: 160,
      render: (value, recode) => {
        return value === "미선택" ? value : `${recode.isDomestic}/${value}`;
      },
    },
    {
      title: "공임비",
      dataIndex: "totalRepairFee",
      key: "totalRepairFee",
      align: "right",
      width: 140,
      render: (value) => `${Number(value as string).toLocaleString()}원`,
    },
    {
      title: "전략상품 추가 공임비",
      dataIndex: "strategyProductFee",
      key: "strategyProductFee",
      align: "right",
      width: 100,
      render: (value) => `${Number(value as string).toLocaleString()}원`,
    },
    {
      title: "정산액(VAT 포함)",
      dataIndex: "settlementAmount",
      key: "settlementAmount",
      align: "right",
      width: 140,
      render: (value) => `${Number(value as string).toLocaleString()}원`,
    },
    {
      title: "정산상태",
      dataIndex: "settlementStatus",
      key: "settlementStatus",
      width: 80,
      align: "center",
      render: (value) => (
        <RepairShopSettlementStatus status={value as SettlementStatusTypes} />
      ),
    },
  ];

  const handleSettlementConfirm = () => {
    const orderNos = selectedList
      ?.filter(
        ({ settlementStatus }) => settlementStatus === "SETTLEMENT_REQUESTED"
      )
      .map(({ orderNo }) => orderNo);

    if (orderNos) {
      mutate({
        orderNos,
      });
    }
  };

  return (
    <Layout style={{ height: "calc(100vh - 57px)" }}>
      <Container openPanel={!!reservation.repairNo}>
        <Typography.Title color={color.gray2} level={1}>
          정산관리
        </Typography.Title>
        <BodyWrapper ref={containerRef}>
          <PanelGroup direction="horizontal">
            <Panel defaultSize={70} minSize={40} style={{ overflowY: "auto" }}>
              <>
                <SettlementFilterForm
                  ref={filterRef}
                  onSearch={(props) =>
                    setFilters((prev) => ({
                      ...prev,
                      ...props,
                    }))
                  }
                />
                {summary && (
                  <OverviewSection>
                    <div>
                      정산 대상 금액은{" "}
                      <b>
                        {(
                          summary.totalRepairFee + summary.strategyProductFee
                        ).toLocaleString()}
                        원
                      </b>
                      입니다.
                    </div>
                    <div>
                      <RepairShopSettlementStatus
                        status="SERVICE_COMPLETED"
                        fee={summary.serviceCompletedFee}
                      />
                      <RepairShopSettlementStatus
                        status="SETTLEMENT_REQUESTED"
                        fee={summary.settlementRequestedFee}
                      />
                      <RepairShopSettlementStatus
                        status="SETTLEMENT_CONFIRMED"
                        fee={summary.settlementConfirmedFee}
                      />
                      <RepairShopSettlementStatus
                        status="PAY_COMPLETED"
                        fee={summary.payCompletedFee}
                      />
                    </div>
                  </OverviewSection>
                )}
                <ListSection>
                  <div className="header_wrapper">
                    <Typography.Title color={color.gray2} level={5}>
                      정산리스트
                    </Typography.Title>

                    <div>
                      <Button
                        type="primary"
                        size="middle"
                        disabled={!haslSettlementRequested}
                        onClick={handleSettlementConfirm}
                      >
                        정산확인
                      </Button>
                    </div>
                  </div>
                  <TableForm
                    size="large"
                    rowKey="orderNo"
                    columns={columns}
                    dataSource={data?.rows || []}
                    rowSelection={{
                      type: "checkbox",
                      fixed: true,
                      selectedRowKeys,
                      onChange: (selectedKey) => {
                        setSelectedRowKeys(selectedKey);
                      },
                    }}
                    onRow={(record) => ({
                      style: {
                        cursor: record ? "pointer" : "default",
                      },
                      onClick: (e) => {
                        e.stopPropagation();
                        if (record && reservation.repairNo !== record.orderNo) {
                          // repairNo 외 정보 제거되는것 방지용 동일 주문 클릭 제어
                          setReservation({ repairNo: record.orderNo });
                        }
                      },
                    })}
                    scroll={{
                      x: 1000,
                      y: scrollY,
                    }}
                    footer={() => (
                      <TableFooter>
                        <div>
                          <div>
                            <p className="selected">
                              선택된 금액{" "}
                              <b>{selectedPrice.toLocaleString()}원</b> (VAT
                              포함)
                            </p>
                            <div>|</div>
                            <p>
                              정산 승인 금액{" "}
                              <b>{payCompletedPrice.toLocaleString()}원</b> (VAT
                              포함)
                            </p>
                          </div>
                        </div>
                        <TableFooterCount total={data?.count} />
                      </TableFooter>
                    )}
                  />
                </ListSection>
              </>
            </Panel>
            {reservation.repairNo && (
              <>
                <PanelResizeHandle
                  style={{
                    height: "100%",
                    width: 4,
                    background: color.gray12,
                    border: `1px solid ${color.gray9}`,
                    borderTopWidth: 0,
                    borderBottomWidth: 0,
                  }}
                />
                <Panel
                  defaultSize={30}
                  minSize={20}
                  maxSize={50}
                  style={{
                    minWidth: 360,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    id="cgInfoRoot"
                    style={{
                      width: "100%",
                      height: "100%",
                      position: "relative",
                    }}
                  >
                    <ReservationDetailDrawer
                      id={reservation.repairNo}
                      onClose={() => {
                        setReservation({ repairNo: undefined });
                      }}
                    />
                  </div>
                </Panel>
              </>
            )}
          </PanelGroup>
        </BodyWrapper>
      </Container>
    </Layout>
  );
}

const Container = styled.div<{ openPanel: boolean }>`
  padding: ${({ openPanel }) => (openPanel ? "36px 0 36px 24px" : "36px 24px")};
  height: 100%;
`;

const BodyWrapper = styled.div`
  margin-top: 36px;
  height: 100%;
`;

const StyledSection = styled.section`
  background-color: white;

  border-radius: 8px;
  :not(:last-of-type) {
    margin-bottom: 24px;
  }
`;

const OverviewSection = styled(StyledSection)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  > div:last-of-type {
    display: flex;
    align-items: center;
    gap: 32px;
    > div {
      display: flex;
      align-items: center;
    }
  }
`;

const ListSection = styled(StyledSection)`
  > div {
    padding: 12px;
  }
  > .header_wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    border-bottom: 1px solid ${theme.colors.gray7};

    > div {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }
`;

const TableFooter = styled.div`
  > div:first-of-type {
    padding: 24px 0;
    margin-bottom: 24px;
    border-bottom: 1px solid ${theme.colors.gray7};
    > div {
      display: flex;
      align-items: center;
      width: fit-content;
      margin-left: auto;
      > p {
        margin-bottom: 0;
        ${theme.typo.body.r14};
      }
      > .selected {
        color: ${theme.colors.mainPrimary3};
      }
      > div {
        margin: 0 16px;
      }
    }
  }
  > div:last-of-type {
    padding: 8px 0;
  }
`;
